import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";
import NewsCard from "@/components/feature/NewsCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getNewsById, getRelatedNews } from "@/utils/content";

export default function NewsDetail() {
  const { id } = useParams();
  const news = getNewsById(id);
  const [toast, setToast] = useState("");

  usePageMeta({
    title: news ? news.title : "资讯详情 - 58动漫",
    description: news ? news.summary : "58动漫资讯详情页，展示动漫行业动态与作品资讯。",
    keywords: news ? `${news.category},${news.title},动漫资讯,58动漫` : "动漫资讯,58动漫",
  });

  useEffect(() => {
    setToast("");
  }, [id]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!news) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 py-16 md:px-8 xl:px-12">
        <EmptyState
          icon="ri-file-unknow-line"
          title="该资讯内容暂时不可用"
          description="这篇资讯可能已被移除或链接有误，去看看其他资讯吧。"
        />
        <div className="mt-6 flex justify-center">
          <Link
            to="/news"
            className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            返回资讯列表
          </Link>
        </div>
      </div>
    );
  }

  const related = getRelatedNews(news, 4);

  const handleShare = (channel: string) => {
    if (channel === "复制链接") {
      try {
        navigator.clipboard?.writeText(window.location.href);
      } catch {
        // ignore
      }
    }
    setToast(`${channel}：分享功能为前端演示`);
  };

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-8 md:py-8 xl:px-12">
      <Breadcrumb
        items={[{ label: "首页", to: "/" }, { label: "动漫资讯", to: "/news" }, { label: news.category }]}
      />

      <article className="mx-auto mt-5 w-full max-w-[860px]">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-background-50">
            {news.category}
          </span>
          <span className="flex items-center gap-1 text-xs text-foreground-500">
            <i className="ri-calendar-line" />
            {news.date}
          </span>
          <span className="flex items-center gap-1 text-xs text-foreground-500">
            <i className="ri-price-tag-3-line" />
            来源：{news.source}
          </span>
        </div>

        <h1 className="mt-4 font-heading text-2xl font-extrabold leading-snug text-foreground-950 md:text-3xl">
          {news.title}
        </h1>

        <p className="mt-4 rounded-lg border-l-4 border-primary-400 bg-primary-50/70 px-4 py-3 text-sm leading-relaxed text-foreground-700">
          {news.summary}
        </p>

        <div className="mt-6 overflow-hidden rounded-lg bg-background-200">
          <div className="aspect-video w-full">
            <img
              src={news.cover}
              alt={news.title}
              title={`${news.title} - 58动漫资讯配图`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        </div>

        <div className="mt-8 flex flex-col gap-4">
          {news.content.map((block, index) => {
            if (block.type === "h") {
              return (
                <h2
                  key={`h-${index}`}
                  className="mt-3 font-heading text-lg font-extrabold text-foreground-950"
                >
                  {block.text}
                </h2>
              );
            }
            if (block.type === "image") {
              return (
                <figure key={`img-${index}`} className="flex flex-col gap-2">
                  <div className="overflow-hidden rounded-lg bg-background-200">
                    <div className="aspect-video w-full">
                      <img
                        src={block.url}
                        alt={block.caption || news.title}
                        title={`${news.title} - 58动漫资讯配图`}
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                  </div>
                  {block.caption && (
                    <figcaption className="text-center text-xs text-foreground-500">
                      {block.caption}
                    </figcaption>
                  )}
                </figure>
              );
            }
            return (
              <p key={`p-${index}`} className="text-sm leading-loose text-foreground-700 md:text-[15px]">
                {block.text}
              </p>
            );
          })}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
          <span className="flex items-center gap-1.5 text-sm font-medium text-foreground-600">
            <i className="ri-share-forward-line text-base text-primary-500" />
            分享这篇资讯：
          </span>
          {[
            { label: "微博", icon: "ri-weibo-fill" },
            { label: "微信", icon: "ri-wechat-fill" },
            { label: "复制链接", icon: "ri-link" },
          ].map((item) => (
            <button
              key={item.label}
              type="button"
              onClick={() => handleShare(item.label)}
              className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-3 py-1.5 text-sm text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <i className={`${item.icon} text-base`} />
              {item.label}
            </button>
          ))}
        </div>
      </article>

      {related.length > 0 && (
        <section className="mx-auto mt-12 w-full max-w-[1080px]" aria-label="相关资讯">
          <h2 className="mb-5 font-heading text-lg font-extrabold text-foreground-950">相关资讯推荐</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {related.slice(0, 3).map((item) => (
              <NewsCard key={item.id} news={item} />
            ))}
          </div>
          <div className="mt-8 flex justify-center">
            <Link
              to="/news"
              className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <i className="ri-arrow-left-line text-base" />
              返回资讯列表
            </Link>
          </div>
        </section>
      )}

      {toast && (
        <div className="fixed bottom-20 left-1/2 z-50 -translate-x-1/2 animate-fade-up rounded-full bg-foreground-950/90 px-5 py-2.5 text-sm text-background-50">
          {toast}
        </div>
      )}
    </div>
  );
}